import ResultHeader from './ResultHeader';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { useEffect, useRef, useState } from 'react';
import ReferenceList from './ReferenceList';
import rehypeRaw from 'rehype-raw';
import Markdown from 'react-markdown';
import Feedback from './Feedback';
import { trimAlternativeSources, buildNotationIndex, applyNotationIndexToContent } from '../../utils/sourcesHelper';
import { toast } from 'react-toastify';
import Banner from '../ui/Banner';
import FollowUpQueryBox from './FollowUpQueryBox';
import FollowUpAnswer from './FollowUpAnswer';
import Loading from '../../components/ui/Loading';
import QuestionLabel from '../ui/QuestionLabel';
import { useNavigate } from 'react-router-dom';
import { sendFollowUpQuestion } from '../../services/query';
import { PROGRESS_DURATION } from '../../constants/common';
import Badge from '../ui/Badge';

const Result = ({
	title = '',
	content,
	references = [],
	elasticDocuments = [],
	conversationId,
	followUpContent = null,
	followUpEnabled = false,
	onFollowCompleteAction,
	id,
	print = false,
	aiModelReference = '',
}) => {
	const { t } = useTranslation();
	const [scrollable, setScrollable] = useState(false);
	const [showFollowUp, setShowFollowUp] = useState(false);
	const [showLoading, setShowLoading] = useState(false);
	const containerRef = useRef(null);
	const navigate = useNavigate();
	const [question, setQuestion] = useState(null);

	// Original question
	const filteredReferences = references.filter((reference) => content.includes(reference.id));
	const notationIndex = buildNotationIndex(content, references);
	const uniqueFilteredReferences = [
		...new Map(
			filteredReferences.map((reference) => [`${reference.id}-${reference.table}-${reference.type}`, reference]),
		).values(),
	];
	const alternativeReferences = references.filter((reference) => !content.includes(reference.id));
	const trimmedAlternativeReferences = trimAlternativeSources(alternativeReferences);

	const hasReliableReferences = filteredReferences && filteredReferences.length > 0;

	// Scroll to bottom
	useEffect(() => {
		const container = containerRef.current;
		const windowHeight = window.innerHeight;

		// verify content height larger than container height
		if (container && container.scrollHeight + 280 > windowHeight) {
			setScrollable(true);
		} else if (container) {
			setScrollable(false);
		}
	}, []);

	// Print the content when the print prop is true
	useEffect(() => {
		// Wait for the content to be rendered
		const timer = setTimeout(() => {
			if (print && content) window.print();
		}, 500);

		return () => clearTimeout(timer);
	}, [print, content, title]);

	const handleScrollToBottom = () => {
		const container = containerRef.current;

		// scroll entire content to bottom
		window.scrollTo({
			top: container.scrollHeight,
			behavior: 'smooth',
		});
	};

	const handleFollowUpSubmit = async (message) => {
		setShowLoading(true);
		setQuestion(message);

		try {
			const data = await sendFollowUpQuestion(message, conversationId);
			if (data.conversationId) {
				setShowFollowUp(false);
				setShowLoading(false);
				if (typeof onFollowCompleteAction === 'function') {
					onFollowCompleteAction(data);
				}
			} else {
				toast.error(t('error.could_not_load_answer'));
				setShowLoading(false);
			}
		} catch (error) {
			toast.error(t('error.server_error'));
			console.error('Error sending follow up question:', error);
			setShowLoading(false);
		}
	};

	const handleLoadingComplete = () => {
		navigate(`/query/${id}`);
		setShowLoading(false);
	};

	const handleAction = () => {
		console.log('Perform action!');
	};

	if (showLoading) {
		return (
			<div className='flex flex-1'>
				<div className='flex flex-col items-center h-[calc(100vh)] pt-20 gap-12'>
					<Loading
						onComplete={handleLoadingComplete}
						completed={followUpContent !== null}
						progressDuration={PROGRESS_DURATION}
						messages={[
							'loading.analyzing_question',
							'loading.retrieving_references',
							'loading.evaluating_relevance',
							'loading.extracting_key_points',
							'loading.formulating_response',
						]}
						question={question}
					/>
					<Banner
						label={t('result_page.notice', {
							app_name: t('general.app_name'),
						})}
						variant='info'
					/>
				</div>
			</div>
		);
	}

	return (
		<div className='w-full pb-28 pt-10' ref={containerRef}>
			<div className='flex flex-col gap-6'>
				<div className='flex flex-col gap-4'>
					<QuestionLabel>
						{aiModelReference && (
							<Badge className='ml-6 leading-none lowercase' variant='primary' size='small'>
								{`${aiModelReference}`}
							</Badge>
						)}
					</QuestionLabel>
					<div className='flex flex-row items-end justify-between gap-6'>
						<h1 className='text-xl font-medium text-on-secondary tracking-wide'>
							{title.charAt(0).toUpperCase() + title.slice(1)}
						</h1>

						{!print && (
							<div className='flex flex-row self-start gap-6 min-w-fit'>
								<Link to={`/query/${id}/print`} target='_blank' rel='noopener noreferrer'>
									<Button variant='tertiary' size='small' type='line'>
										{t('result_page.print')}
									</Button>
								</Link>
							</div>
						)}
					</div>
				</div>

				<div className='flex flex-col'>
					<ResultHeader content={applyNotationIndexToContent(content, notationIndex)} print={print} />

					<div className='flex flex-col gap-8 pb-5'>
						<div data-ai-generated='true' className='pr-20 font-base leading-[1.4] tracking-wide'>
							<Markdown
								rehypePlugins={[rehypeRaw]}
								components={{
									ul: ({ children }) => <ul className='list-disc'>{children}</ul>,
									ol: ({ children }) => <ol className='list-decimal'>{children}</ol>,
									li: ({ children }) => <li className='ml-4 mb-4'>{children}</li>,
									h1: ({ children }) => <h1 className='text-xl font-medium'>{children}</h1>,
									h2: ({ children }) => <h2 className='text-lg font-medium'>{children}</h2>,
									h3: ({ children }) => <h3 className='text-lg font-medium'>{children}</h3>,
									h4: ({ children }) => <h4 className='text-lg font-medium'>{children}</h4>,
									h5: ({ children }) => <h5 className='text-lg font-medium'>{children}</h5>,
									p: ({ children }) => <p className='mb-4 text-md font-normal'>{children}</p>,
									a: ({ children, node }) => {
										const href = node?.properties?.href || '';
										return (
											<a href={href} className='text-primary'>
												{children}
											</a>
										);
									},
									strong: ({ children }) => <span className='font-medium'>{children}</span>,
								}}
							>
								{hasReliableReferences
									? applyNotationIndexToContent(content, notationIndex)
									: t('result_page.no_reply', { app_name: t('general.app_name') })}
							</Markdown>
						</div>

						<ReferenceList
							title={t('general.references')}
							references={uniqueFilteredReferences}
							print={print}
							notationIndex={notationIndex}
							elasticDocuments={elasticDocuments}
						/>
						{hasReliableReferences && !print && (
							<ReferenceList
								title={t('general.alternative_sources')}
								references={trimmedAlternativeReferences}
								className='mt-0'
								collapsible={true}
								defaultCollapsed={true}
								notationIndex={notationIndex}
								elasticDocuments={elasticDocuments}
							/>
						)}

						{print && followUpContent && <div style={{ pageBreakBefore: 'always' }}></div>}

						{followUpEnabled && !showFollowUp && !followUpContent && conversationId && !print && (
							<div className='flex flex-row items-end gap-6'>
								<Button
									variant='primary'
									size='medium'
									type='solid'
									onClick={() => setShowFollowUp(true)}
								>
									{t('result_page.follow_up.show_button')}
								</Button>
							</div>
						)}
						{followUpEnabled && showFollowUp && !followUpContent && conversationId && !print && (
							<FollowUpQueryBox
								title={t('result_page.follow_up.title')}
								size='medium'
								onSubmit={handleFollowUpSubmit}
								showActionButton={true}
								onAction={handleAction}
							/>
						)}
						{followUpContent && (
							<FollowUpAnswer
								title={t('result_page.follow_up.answer_title')}
								question={followUpContent.question}
								answer={followUpContent.answer}
								references={followUpContent.sources}
								elasticDocuments={followUpContent.elasticDocuments}
								aiModelReference={followUpContent.aiModelReference}
								print={print}
							/>
						)}

						{hasReliableReferences && conversationId && !print && (
							<Feedback
								title={t('result_page.feedback.title')}
								subtitle={t('result_page.feedback.subtitle')}
								submitText={t('result_page.feedback.submit')}
								placeholder={t('result_page.feedback.placeholder')}
								closeText={t('result_page.feedback.close')}
								conversationId={conversationId}
								defaultOpen={false}
							/>
						)}
					</div>
				</div>

				{print && <div style={{ pageBreakBefore: 'always' }}></div>}

				{hasReliableReferences && (
					<Banner
						label={t('result_page.notice', {
							app_name: t('general.app_name'),
						})}
						variant='info'
					/>
				)}

				<div className='fixed bottom-0 w-full h-28 bg-gradient-to-t from-white to-transparent pointer-events-none' />
			</div>

			{scrollable && !print && (
				<div className='fixed bottom-4 right-4'>
					<Button onClick={handleScrollToBottom} size='medium' variant='primary' type='solid'>
						<i className='ri-arrow-down-line text-sm leading-[1.4]' />
					</Button>
				</div>
			)}
		</div>
	);
};

Result.propTypes = {
	title: PropTypes.string.isRequired,
	content: PropTypes.string.isRequired,
	references: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string.isRequired,
			link: PropTypes.string.isRequired,
		}),
	),
	elasticDocuments: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			title: PropTypes.string,
			table: PropTypes.string,
			type: PropTypes.string,
			highlight: PropTypes.shape({
				subChunks: PropTypes.arrayOf(PropTypes.string),
			}),
			updatedAt: PropTypes.string,
			createdAt: PropTypes.string,
			chunks: PropTypes.arrayOf(PropTypes.string),
		}),
	),
	conversationId: PropTypes.number,
	id: PropTypes.string.isRequired,
	followUpContent: PropTypes.object,
	followUpEnabled: PropTypes.bool,
	onFollowCompleteAction: PropTypes.func,
	aiModelReference: PropTypes.string,
	print: PropTypes.bool,
};

export default Result;
